import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useNavigate } from "@tanstack/react-router";
import React, { useContext, useEffect } from "react";
import { ConfigContext, OrderDataContext, ReturnOrderContext } from "@/Layout";
import { LineItem } from "@/api/verify";
import FloatLogo from "@/components/FloatLogo/FloatLogo";
import { Input } from "@/components/Input/Input";
import LoadSpinner from "@/components/LoadSpinner/LoadSpinner";
import NextButton from "@/components/NextButton/NextButton";
import { ReturnContainer } from "@/components/ReturnContainer/ReturnContainer";
import { cn, getErrorMessage } from "@/utils/utils";
import { isValidEmail } from "@/validation/email";
import { isValidOrderName } from "@/validation/order-name";

export const Route = createFileRoute("/")({
  component: FrontPage,
});

export function FrontPage() {
  const [orderName, setOrderName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [isUsingQueryParams, setIsUsingQueryParams] = React.useState(false);
  const configData = useContext(ConfigContext);
  const { setOrderData } = useContext(OrderDataContext);
  const { setReturnOrder } = useContext(ReturnOrderContext);

  const navigate = useNavigate();

  useEffect(() => {
    setReturnOrder(undefined);
  }, [setReturnOrder]);

  const { data, isLoading, refetch, error } = useQuery({
    queryKey: ["verify"],
    queryFn: () =>
      fetch(`${import.meta.env.VITE_API_URL}/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          order_name: orderName,
          email: email,
          shop: configData?.shop,
          portal_id: configData?.portal_id,
        }),
      }).then(async (res) => {
        if (res.ok) return res.json();
        if (!res.ok && res.status === 400) {
          const result = await res.json();
          const error = result["error"];
          throw new Error(error);
        }
        throw new Error("Something went wrong");
      }),
    retry: false,
    enabled: false,
  });

  useEffect(() => {
    if (data) {
      const head = document.head;
      data.line_items.forEach((item: LineItem) => {
        const link = document.createElement("link");
        link.rel = "prefetch";
        link.href = item.product_src;
        link.as = "image";
        link.crossOrigin = "anonymous";
        head.appendChild(link);
      });
    }
    setOrderData(data);
  }, [data, setOrderData]);

  useEffect(() => {
    if (data) {
      navigate({
        to: "/products",
        search: (prev) => prev,
      });
    }
  }, [data, navigate]);

  const isPrefixedWith = (value: string, prefix: string): boolean => {
    return (
      (value.length < prefix.length && prefix.startsWith(value)) ||
      value.startsWith(prefix)
    );
  };

  const removePrefix = (value: string, prefix: string): string => {
    for (let i = 0; i < Math.min(value.length, prefix.length); i++) {
      if (value[i] != prefix[i]) return value.slice(i);
    }
    return "";
  };

  /**
   * Help the customer by automatically adding the order prefix if not present
   * For exchanges, we break the prefix rule so we can't help them add it there
   */
  const onOrderNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const orderNumber = e.target.value.trim();
    if (!configData?.order_prefix) return setOrderName(orderNumber);
    const prefix = configData?.order_prefix.toUpperCase();
    const exchangePrefix = "EXC";
    const value = orderNumber.toUpperCase();

    if (isPrefixedWith(value, exchangePrefix)) {
      return setOrderName(value);
    }

    if (isPrefixedWith(value, prefix)) {
      return setOrderName(value);
    }

    setOrderName(prefix + removePrefix(value, prefix));
  };

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const tryMatchUrlParam = (
    url: string,
    param: string
  ): RegExpMatchArray | null => {
    const pattern = new RegExp(`${param}=(.*?)(&|$)`);
    return url.match(pattern);
  };

  const getStringFromMatch = (match: RegExpMatchArray): string => {
    return match[1];
  };

  const startReturn = () => {
    refetch();
  };

  useEffect(() => {
    const url = window.location.href;
    const orderNameUrlParam = tryMatchUrlParam(url, "orderName");
    const emailUrlParam = tryMatchUrlParam(url, "email");

    if (orderNameUrlParam && emailUrlParam) setIsUsingQueryParams(true);
    if (orderNameUrlParam) setOrderName(getStringFromMatch(orderNameUrlParam));
    if (emailUrlParam) setEmail(getStringFromMatch(emailUrlParam));
  }, []);

  useEffect(() => {
    if (!isUsingQueryParams) return;

    if (orderName !== "" && email != "" && configData) refetch();
  }, [orderName, email, configData, isUsingQueryParams, refetch]);

  const canReturnBeStarted =
    isValidOrderName(orderName) && isValidEmail(email) && !isLoading;

  useEffect(() => {
    const enableStartReturnWithEnter = (e: KeyboardEvent) => {
      if (e.key === "Enter" && canReturnBeStarted) {
        refetch();
      }
    };

    document.addEventListener("keypress", enableStartReturnWithEnter);

    return () => {
      document.removeEventListener("keypress", enableStartReturnWithEnter);
    };
  }, [canReturnBeStarted, refetch]);

  if (!configData) return null;

  return (
    <ReturnContainer>
      <div className="flex flex-col h-full">
        <div>
          <img
            className="min-w-24 max-w-36 cursor-pointer"
            src={configData?.logo_src}
            onClick={() => window.open(configData?.website_url, "_blank")}
          ></img>
        </div>
        <p className="flex-wrap text-sm wrap sm:max-w-[400px] mt-4 whitespace-pre-line">
          {configData?.start_return_message}
        </p>
        <div className="mt-20 grid gap-3 mb-3">
          <div className="relative">
            <Input
              placeholder={configData?.order_placeholder_text}
              text={orderName}
              onChange={onOrderNumberChange}
            />
            {configData.order_tooltip_text && (
              <div className="group">
                <div className="absolute -right-0 top-3.5 mr-2">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="size-8"
                  >
                    <rect
                      x="0.25"
                      y="0.25"
                      width="11.5"
                      height="11.5"
                      rx="5.75"
                      stroke="#AFAFAF"
                      strokeWidth="0.5"
                    />
                    <path
                      // eslint-disable-next-line max-len
                      d="M5.498 7.551C5.498 7.124 5.589 6.914 6.1 6.697L6.345 6.592C6.842 6.375 7.115 6.032 7.115 5.528C7.115 4.919 6.723 4.541 6.086 4.541C5.428 4.541 4.952 4.94 4.938 5.598H4.28C4.294 4.625 5.064 4.002 6.093 4.002C7.22 4.002 7.773 4.744 7.773 5.507C7.773 6.242 7.262 6.753 6.569 7.047L6.471 7.089C6.163 7.222 6.128 7.376 6.128 7.642V7.88H5.498V7.551ZM5.463 9V8.3H6.163V9H5.463Z"
                      fill="#AFAFAF"
                    />
                  </svg>
                </div>
                <div
                  className={cn(
                    "invisible group-hover:visible absolute right-0 z-10 w-64 p-2",
                    "mt-2 text-sm text-black bg-white border rounded shadow-lg"
                  )}
                >
                  <p className="break-words whitespace-pre-line font-light">
                    {configData.order_tooltip_text}
                  </p>
                </div>
              </div>
            )}
          </div>
          <Input
            type="email"
            placeholder="E-mail"
            text={email}
            onChange={onEmailChange}
          />
        </div>
        <div className="">
          <NextButton
            text={configData.start_return_button_message}
            onClick={startReturn}
            buttonDisabled={!canReturnBeStarted}
            buttonColor={configData?.button_color}
          />
        </div>
        <div className="relative items-center min-h-8">
          {error && (
            <div className="text-red-500 text-center">
              <span className="text-sm">
                {getErrorMessage(error, configData)}
              </span>
            </div>
          )}
          {isLoading && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <LoadSpinner />
            </div>
          )}
        </div>
        <div className="text-xs mt-auto sm:mt-[90px]">
          <p className="break-words sm:max-w-[500px]">
            {configData?.need_help_message}
          </p>
          <hr className="mt-2" />
          <div className="flex mt-2 items-center">
            <span>Powered by </span>
            <FloatLogo className="w-10" />
          </div>
        </div>
      </div>
    </ReturnContainer>
  );
}
